import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';
import { 
  FileText, ArrowLeft, Loader2, Plus, Search, Edit, Trash2, Eye, 
  Calendar, RefreshCw, MoreVertical 
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  topic: string;
  excerpt: string | null;
  main_image_url: string | null;
  published_at: string;
  is_published: boolean;
  created_at: string;
}

interface SiteSettings {
  id: string;
  name: string;
  blog_enabled: boolean;
  allow_multiple_blog_posts_per_day: boolean;
  last_blog_created_date: string | null;
}

const CATEGORIES = [
  'Buyers', 'Sellers', 'Investor', 'Local Market and Geography',
  'Lifestyle and Community', 'Food and Drink', 'Family and Kids',
  'Education and Schools', 'Health and Wellness', 'Work, Business and Economy',
  'Housing and Homeownership', 'Legal, Financial and Planning', 'Relocation and Moving',
  'Community and Civic', 'Seasonal and Time Based', 'Trust and Authority',
  'Buyer and Seller Journey Stage',
];

export default function BlogManage() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [site, setSite] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
    if (user && !globalAdminLoading && selectedSiteId) {
      fetchData();
    }
  }, [user, authLoading, globalAdminLoading, selectedSiteId, navigate]);

  const fetchData = async () => {
    if (!selectedSiteId) {
      setLoading(false);
      return;
    }

    try {
      const [siteRes, postsRes] = await Promise.all([
        supabase.from('sites').select('id, name, blog_enabled, allow_multiple_blog_posts_per_day, last_blog_created_date').eq('id', selectedSiteId).single(),
        supabase.from('blog_posts').select('*').eq('site_id', selectedSiteId).order('published_at', { ascending: false })
      ]);

      if (siteRes.data) setSite(siteRes.data as SiteSettings);
      if (postsRes.data) setPosts(postsRes.data as BlogPost[]);
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!site) return;

    // Check one post per day rule (unless override is enabled)
    const today = new Date().toISOString().split('T')[0];
    if (!site.allow_multiple_blog_posts_per_day && site.last_blog_created_date === today) {
      toast({
        title: 'Daily limit reached',
        description: 'You can only create one blog post per day. Enable "Allow Multiple Posts Per Day" in settings to override.',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-blog-post', {
        body: { siteId: site.id, manual: true }
      });

      if (error) throw error;

      toast({
        title: 'Blog post created',
        description: 'Your new blog post has been generated.',
      });

      // Navigate to edit the new post
      if (data?.postId) {
        navigate(`/admin/blog/${data.postId}/edit`);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: 'Error',
        description: 'Failed to create blog post. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePost = async () => {
    if (!deletePostId || !site) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', deletePostId);

      if (error) throw error;

      setPosts(posts.filter(p => p.id !== deletePostId));
      
      // Trigger auto-indexnow to update sitemap
      try {
        await supabase.functions.invoke('auto-indexnow', {
          body: {
            siteId: site.id,
            trigger: 'blog_deleted',
          },
        });
      } catch (indexError) {
        console.error('Failed to trigger indexnow:', indexError);
      }

      toast({
        title: 'Post deleted',
        description: 'The blog post has been removed.',
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete post.',
        variant: 'destructive',
      });
    } finally {
      setDeletePostId(null);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.topic.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || post.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !selectedSiteId) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">No site selected</p>
      </div>
    );
  }

  if (!site) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <FileText className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Blog Management</span>
            </div>
            <SiteSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{posts.length}</div>
              <p className="text-sm text-muted-foreground">Total Posts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{posts.filter(p => p.is_published).length}</div>
              <p className="text-sm text-muted-foreground">Published</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{posts.filter(p => new Date(p.published_at) > new Date()).length}</div>
              <p className="text-sm text-muted-foreground">Scheduled</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {site.last_blog_created_date 
                  ? format(new Date(site.last_blog_created_date), 'MMM d') 
                  : 'Never'}
              </div>
              <p className="text-sm text-muted-foreground">Last Created</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleCreatePost} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create New Post
              </>
            )}
          </Button>
        </div>

        {/* Posts List */}
        <Card>
          <CardHeader>
            <CardTitle>Blog Posts</CardTitle>
            <CardDescription>
              {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No blog posts yet.</p>
                <p className="text-sm">Create your first post to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPosts.map((post) => (
                  <div 
                    key={post.id} 
                    className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    {/* Thumbnail */}
                    <div className="w-20 h-14 rounded overflow-hidden bg-muted shrink-0">
                      {post.main_image_url ? (
                        <img src={post.main_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{post.title}</h3>
                        {!post.is_published && (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                        {new Date(post.published_at) > new Date() && (
                          <Badge variant="outline">Scheduled</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">{post.category}</Badge>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(post.published_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/blog/${post.id}/preview`}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/blog/${post.id}/edit`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => setDeletePostId(post.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletePostId} onOpenChange={() => setDeletePostId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The blog post will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePost} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}